// src/context/AuthContext.tsx
import axios from "axios";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { useLocation } from "react-router-dom";
import {
  ReportPreferences,
  SubscriptionStatus,
  SubscriptionStatusType,
  User,
} from "../db/schema";
import { isAuthPage, isAuthPath } from "../utilities/isAuthPage";
import { isEntitledStatus } from "../utilities/subscriptionAccess";

export interface UserWithRoles extends User {
  roles?: string[];
}

export type AddonEntitlement = {
  entitled: boolean;
  reason?: string;
  seatsPurchased?: number;
  seatsUsed?: number;
  currentPeriodEnd?: string | null;
  allowanceCalls?: number | null;
  allowanceMinutes?: number | null;
  pricePerSeat?: number;
  pricePerSeatAnnual?: number;
  annualAvailable?: boolean;
};

export type AddonEntitlements = {
  investigations?: AddonEntitlement;
  // Footage analysis on case reports (the video tier).
  video?: AddonEntitlement & { hoursPerSeat?: number };
};

interface AuthContextType {
  isAuthenticated: boolean;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  user: UserWithRoles | null;
  setUser: React.Dispatch<React.SetStateAction<UserWithRoles | null>>;
  loading: boolean;
  subscriptionStatus: (typeof SubscriptionStatus.enumValues)[number] | null;
  subscriptionCurrentPeriodEnd: Date | null;
  setSubscriptionStatus: React.Dispatch<
    React.SetStateAction<(typeof SubscriptionStatus.enumValues)[number] | null>
  >;
  setSubscriptionCurrentPeriodEnd: React.Dispatch<
    React.SetStateAction<Date | null>
  >;
  refreshSubscriptionStatus: () => Promise<
    (typeof SubscriptionStatus.enumValues)[number] | null
  >;
  subscriptionPlatform: string | null;
  // True when the user's agency had a promo-code trial that has expired
  // (and no other subscription is active) — used to explain the paywall.
  agencyTrialExpired: boolean;
  agencyTrialEndedAt: Date | null;
  // Per-seat add-ons resolved by /api/subscription/status. The client uses
  // this to hide menu items; the server's requireAddon middleware is the
  // actual control.
  addons: AddonEntitlements;
  refreshAddons: () => Promise<void>;
  reportPreferences: ReportPreferences | null;
  setReportPreferences: React.Dispatch<
    React.SetStateAction<ReportPreferences | null>
  >;
  resetAuthState: () => void;
  checkAuthStatus: () => Promise<void>;
  mfaRequired: boolean;
  mfaVerified: boolean;
  mfaConfigured: boolean;
  setMfaVerified: (verified: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<UserWithRoles | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState<
    (typeof SubscriptionStatus.enumValues)[number] | null
  >(null);
  const [subscriptionCurrentPeriodEnd, setSubscriptionCurrentPeriodEnd] =
    useState<Date | null>(null);
  const [subscriptionPlatform, setSubscriptionPlatform] = useState<
    string | null
  >(null);
  const [agencyTrialExpired, setAgencyTrialExpired] = useState(false);
  const [agencyTrialEndedAt, setAgencyTrialEndedAt] = useState<Date | null>(
    null
  );
  const [reportPreferences, setReportPreferences] =
    useState<ReportPreferences | null>(null);
  const [addons, setAddons] = useState<AddonEntitlements>({});
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaVerified, setMfaVerified] = useState(true);
  const [mfaConfigured, setMfaConfigured] = useState(false);

  // Only swap the addons object when its contents change. Consumers effect
  // off this value, and AuthProvider re-rendering with a fresh object each
  // poll would re-run them (EditorMenuBar refreshes subscription status on
  // mount, which is exactly the loop that produced 300 requests/2 min).
  const setAddonsIfChanged = useCallback((next: AddonEntitlements) => {
    setAddons((prev) =>
      JSON.stringify(prev) === JSON.stringify(next) ? prev : next,
    );
  }, []);

  // Stable identity: EditorMenuBar lists this in a useEffect dependency
  // array, so a per-render function would re-fetch on every render.
  const refreshSubscriptionStatus = useCallback(async (): Promise<
    (typeof SubscriptionStatus.enumValues)[number] | null
  > => {
    try {
      const subscriptionResponse = await axios.get("/api/subscription/status");
      setSubscriptionStatus(subscriptionResponse.data.status);
      setSubscriptionCurrentPeriodEnd(
        subscriptionResponse.data.currentPeriodEnd
      );
      setAddonsIfChanged(subscriptionResponse.data.addons || {});
      setAgencyTrialExpired(subscriptionResponse.data.agencyTrialExpired || false);
      setAgencyTrialEndedAt(
        subscriptionResponse.data.agencyTrialEndedAt
          ? new Date(subscriptionResponse.data.agencyTrialEndedAt)
          : null
      );
      return subscriptionResponse.data.status;
    } catch (error) {
      console.error("Error refreshing subscription status:", error);
      return null;
    }
  }, [setAddonsIfChanged]);

  const refreshAddons = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/subscription/status");
      setAddonsIfChanged(data.addons || {});
    } catch (error) {
      console.error("Error refreshing add-ons:", error);
    }
  }, []);

  const resetAuthState = useCallback(() => {
    setIsAuthenticated(false);
    setUser(null);
    setSubscriptionStatus(null);
    setAddons({});
    setAgencyTrialExpired(false);
    setAgencyTrialEndedAt(null);
    setReportPreferences(null);
    setMfaRequired(false);
    setMfaVerified(true);
    setMfaConfigured(false);
  }, []);

  const checkAuthStatus = useCallback(async () => {
    try {
      const response = await axios.get("/api/auth/user");
      if (response.data.user) {
        setIsAuthenticated(true);
        // Ensure roles are included in the user object
        const userWithRoles = {
          ...response.data.user,
          roles: response.data.user.roles || []
        };
        setUser(userWithRoles);

        // Set MFA state from server response
        const isMfaRequired = response.data.mfaRequired || false;
        const isMfaVerified = response.data.mfaVerified ?? true;
        setMfaRequired(isMfaRequired);
        setMfaVerified(isMfaVerified);
        setMfaConfigured(response.data.mfaConfigured || false);

        // Skip fetching subscription/preferences if MFA is required but not yet verified
        // (these endpoints are blocked by the MFA guard middleware)
        if (isMfaRequired && !isMfaVerified) {
          return;
        }

        // Fetch subscription status
        const subscriptionResponse = await axios.get(
          "/api/subscription/status"
        );
        setSubscriptionStatus(subscriptionResponse.data.status);
        setSubscriptionCurrentPeriodEnd(
          subscriptionResponse.data.currentPeriodEnd
        );
        setSubscriptionPlatform(subscriptionResponse.data.platform);
        setAddonsIfChanged(subscriptionResponse.data.addons || {});
        setAgencyTrialExpired(
          subscriptionResponse.data.agencyTrialExpired || false
        );
        setAgencyTrialEndedAt(
          subscriptionResponse.data.agencyTrialEndedAt
            ? new Date(subscriptionResponse.data.agencyTrialEndedAt)
            : null
        );

        // Fetch report preferences
        const preferencesResponse = await axios.get(
          "/api/settings/report-preferences"
        );
        setReportPreferences(preferencesResponse.data.preferences);

        // Only redirect if not on subscription or trial-related pages
        const isSubscriptionPage =
          window.location.pathname.includes("/subscription");
        const isConfirmationPage =
          window.location.pathname.includes("/confirmation");

        if (!isSubscriptionPage && !isConfirmationPage) {
          if (!subscriptionResponse.data.status) {
            window.location.href = "/subscription";
          }
          // Redirect if subscription is incomplete or expired
          else if (
            subscriptionResponse.data.status !==
              SubscriptionStatusType.enum.trialing &&
            subscriptionResponse.data.status !==
              SubscriptionStatusType.enum.active
          ) {
            window.location.href = "/subscription";
          }
        }
      }
    } catch (error) {
      resetAuthState();
    } finally {
      setLoading(false);
    }
  }, [resetAuthState]);

  useEffect(() => {
    if (!isAuthPage) {
      checkAuthStatus();
    } else {
      setLoading(false);
    }
  }, [checkAuthStatus]);

  // The check above is skipped when the app loads on /login or /signup, and
  // those pages move into the app with a client-side navigate(), so nothing
  // re-runs it. Catch that hand-off here: the first time such a page lands
  // on an app route, confirm the subscription and send a lapsed officer to
  // the paywall. Deliberately narrower than checkAuthStatus (no auth reset on
  // a failed fetch): a flaky network must not log out an officer who just
  // signed in. The API enforces the paywall regardless (402).
  const { pathname } = useLocation();
  const needsEntryCheck = useRef(isAuthPage);
  useEffect(() => {
    if (!needsEntryCheck.current || isAuthPath(pathname)) return;
    if (
      pathname.includes("/subscription") ||
      pathname.includes("/confirmation") ||
      pathname.includes("/mfa-")
    ) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get("/api/subscription/status");
        if (cancelled) return;
        needsEntryCheck.current = false;
        if (!isEntitledStatus(data.status)) {
          window.location.href = "/subscription";
        }
      } catch {
        // Not signed in, MFA pending, or offline: nothing to enforce here.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pathname]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        setIsAuthenticated,
        user,
        setUser,
        loading,
        subscriptionStatus,
        setSubscriptionStatus,
        subscriptionCurrentPeriodEnd,
        setSubscriptionCurrentPeriodEnd,
        refreshSubscriptionStatus,
        subscriptionPlatform,
        addons,
        refreshAddons,
        agencyTrialExpired,
        agencyTrialEndedAt,
        reportPreferences,
        setReportPreferences,
        resetAuthState,
        checkAuthStatus,
        mfaRequired,
        mfaVerified,
        mfaConfigured,
        setMfaVerified,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
